import { Button } from "@/design-system/Button/Button";
import { CompletedChip } from "@/design-system/CompletedChip/CompletedChip";
import { useStartClubImport } from "@/features/MyClub/EmptyClub/useStartClubImport";
import { getUserSelector } from "@/redux/user/userSlice";
import type { ClubImportOrigin } from "@repo/shared/redux";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";

/**
 * A toolbar row for pages that read the club: an import button with a line
 * saying what the page then does with the club, or once imported a chip that
 * reports it.
 *
 * The line is there to talk someone into importing, so it goes once they
 * have, and it never appears on a phone, where it costs more rows than it is
 * worth. The state itself is always shown.
 */
export const ClubImportRow = ({
  isTouch,
  origin,
  importLabel,
  importedLabel,
  pitch,
}: {
  isTouch: boolean;
  origin: ClubImportOrigin;
  importLabel: string;
  importedLabel: string;
  pitch: string;
}) => {
  const { pathname } = useLocation();
  const startClubImport = useStartClubImport(origin);
  const { user } = useSelector(getUserSelector);

  /*
    Players in the club, not merely an import on record: an account that
    imported once and has since emptied its club still has nothing to solve
    from. Counted explicitly rather than through `clubIsEmptySelector`, whose
    `=== 0` reads as "not empty" in the window before the profile lands — which
    would flash the done state at someone who has never imported.
  */
  const imported = Boolean(user.uuid) && user.playerCount > 0;
  const showPitch = !imported && !isTouch;

  return (
    <div className="flex w-full min-w-0 flex-row flex-wrap items-center gap-x-2 gap-y-1">
      {/*
        With a club in place there is nothing here to press, so this stops
        being a control and becomes a chip that reports the state. Without
        one it is the same quiet button Add players and the alternatives pill
        wear. Re-importing lives in My Club, where a stale club is dealt with.
      */}
      {imported ? (
        <CompletedChip className="shrink-0">{importedLabel}</CompletedChip>
      ) : (
        <Button
          variant="tertiary"
          size="small"
          /* Returning users on a capable browser import right here; a
             first-timer goes through the tutorial and is sent back here after. */
          onClick={() => startClubImport({ returnTo: pathname })}
          className="shrink-0"
        >
          {importLabel}
        </Button>
      )}

      {showPitch && (
        <>
          <span aria-hidden className="text-foreground-muted">
            &bull;
          </span>
          <p className="text-sm text-foreground-muted">{pitch}</p>
        </>
      )}
    </div>
  );
};
